import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import type { Contact } from "@domaddress/shared";
import {
  CONTACT_RELATION_CODES,
  CONTACT_RELATION_LABELS_NL,
  getAutoReverseRelationLabel,
  getReverseChoiceOptions,
  relationNeedsReverseChoice,
  type ContactRelationCode,
} from "@domaddress/shared";
import SearchableSelect, { type SearchableSelectOption } from "./SearchableSelect";
import {
  ALLE_CONTACTEN_API,
  DOMINICUSGANGERS_API,
  EXTERNE_CONTACTEN_API,
  LEVERANCIERS_API,
  PERSONEEL_API,
  VRIJWILLIGERS_API,
} from "../lib/contactApiPaths";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Kaart waar de relatie op wordt gezet */
  contactId: number;
  /** REST-basis van die kaart, zelfde als ContactViewModal */
  apiPath: string;
  onAdded: () => void;
};

function entityKindPickerGroup(kind: string | null | undefined): string {
  switch (kind) {
    case "personeel":
      return "Personeel";
    case "vrijwilliger":
      return "Vrijwilliger";
    case "dominicusganger":
      return "Dominicusganger";
    case "extern":
      return "Extern contact";
    case "leverancier":
      return "Leverancier";
    case "emaillijst":
      return "E-mailabonnee";
    default:
      return "Overig";
  }
}

async function fetchContactsForPicker(excludeId: number): Promise<Contact[]> {
  const sortNl = (a: Contact, b: Contact) =>
    a.last_name.localeCompare(b.last_name, "nl") || a.first_name.localeCompare(b.first_name, "nl");

  try {
    const res = await api.get<{ data: Contact[] }>(ALLE_CONTACTEN_API, { params: {} });
    const rows = res.data.data ?? [];
    if (rows.length > 0) {
      return rows.filter((c) => c.id !== excludeId).sort(sortNl);
    }
  } catch {
    /* geen toegang tot volledig adresboek — per lijst proberen */
  }

  const out: Contact[] = [];
  const merge = (arr: Contact[]) => {
    for (const c of arr) {
      if (c.id === excludeId) continue;
      if (!out.some((x) => x.id === c.id)) out.push(c);
    }
  };
  for (const path of [
    VRIJWILLIGERS_API,
    PERSONEEL_API,
    DOMINICUSGANGERS_API,
    EXTERNE_CONTACTEN_API,
    LEVERANCIERS_API,
  ]) {
    try {
      const res = await api.get<{ data: Contact[] }>(path, { params: {} });
      merge(res.data.data ?? []);
    } catch {
      /* geen rechten op deze lijst */
    }
  }
  return out.sort(sortNl);
}

function contactLabel(c: Contact) {
  const t = c.tussenvoegsel?.trim();
  return [c.first_name, t, c.last_name]
    .filter((x) => x != null && String(x).trim() !== "")
    .map((x) => String(x).trim())
    .join(" ");
}

/**
 * Modal: kies een andere kaart + relatietype; slaat op via POST …/relations.
 */
export default function ContactRelationAddModal({ open, onClose, contactId, apiPath, onAdded }: Props) {
  const [pickerContacts, setPickerContacts] = useState<Contact[]>([]);
  const [otherId, setOtherId] = useState("");
  const [relationCode, setRelationCode] = useState<ContactRelationCode>("overig");
  /** Alleen bij `relationNeedsReverseChoice`: hoe jij op *hun* kaart genoemd wordt. */
  const [reverseRelationCode, setReverseRelationCode] = useState<ContactRelationCode>("zoon");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setOtherId("");
    setRelationCode("overig");
    (async () => {
      const list = await fetchContactsForPicker(contactId);
      setPickerContacts(list);
    })();
  }, [open, contactId]);

  useEffect(() => {
    const opts = getReverseChoiceOptions(relationCode);
    if (opts.length > 0) setReverseRelationCode(opts[0]);
  }, [relationCode]);

  const options = useMemo((): SearchableSelectOption[] => {
    return pickerContacts.map((c) => ({
      value: String(c.id),
      label: contactLabel(c),
      description: (c.email_prive || c.email)?.trim() || undefined,
      group: entityKindPickerGroup(c.entity_kind),
    }));
  }, [pickerContacts]);

  if (!open) return null;

  async function save() {
    const oid = Number(otherId);
    if (!oid || !Number.isFinite(oid)) {
      setErr("Kies een persoon uit de lijst.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body: {
        other_contact_id: number;
        relation_code: ContactRelationCode;
        reverse_relation_code?: ContactRelationCode;
      } = { other_contact_id: oid, relation_code: relationCode };
      if (relationNeedsReverseChoice(relationCode)) {
        body.reverse_relation_code = reverseRelationCode;
      }
      await api.post(`${apiPath}/${contactId}/relations`, body);
      onAdded();
      onClose();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErr(msg || "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Relatie toevoegen</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Sluiten">
            ✕
          </button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {err && <p className="form-error" style={{ margin: 0 }}>{err}</p>}
          <div className="form-group">
            <label>Andere persoon (zoek in adresboek)</label>
            <SearchableSelect
              value={otherId}
              onValueChange={setOtherId}
              options={options}
              placeholder="Typ naam of e-mail…"
              emptyMessage="Geen resultaat — probeer een andere spelling."
              aria-label="Persoon kiezen"
            />
          </div>
          <div className="form-group">
            <label>Soort relatie (deze persoon t.o.v. jouw kaart)</label>
            <select
              className="search-input"
              style={{ width: "100%", padding: "9px 12px" }}
              value={relationCode}
              onChange={(e) => setRelationCode(e.target.value as ContactRelationCode)}
            >
              {CONTACT_RELATION_CODES.map((code) => (
                <option key={code} value={code}>
                  {CONTACT_RELATION_LABELS_NL[code]}
                </option>
              ))}
            </select>
            <small style={{ fontSize: "0.78rem", color: "var(--text-500)", marginTop: 4, display: "block" }}>
              Voorbeeld: kies <strong>Moeder</strong> als de gekozen persoon jouw moeder is.
            </small>
          </div>
          {relationNeedsReverseChoice(relationCode) && (
            <div className="form-group">
              <label>Op hun kaart: jij bent hun…</label>
              <select
                className="search-input"
                style={{ width: "100%", padding: "9px 12px" }}
                value={reverseRelationCode}
                onChange={(e) => setReverseRelationCode(e.target.value as ContactRelationCode)}
              >
                {getReverseChoiceOptions(relationCode).map((code) => (
                  <option key={code} value={code}>
                    {CONTACT_RELATION_LABELS_NL[code]}
                  </option>
                ))}
              </select>
              <small style={{ fontSize: "0.78rem", color: "var(--text-500)", marginTop: 4, display: "block" }}>
                Kies wat klopt (bijv. <strong>zoon</strong> of <strong>dochter</strong>) — dit wordt automatisch op de andere kaart gezet.
              </small>
            </div>
          )}
          {!relationNeedsReverseChoice(relationCode) && getAutoReverseRelationLabel(relationCode) && (
            <p style={{ fontSize: "0.78rem", color: "var(--text-500)", margin: 0, lineHeight: 1.45 }}>
              Op hun kaart zetten we automatisch: <strong>{getAutoReverseRelationLabel(relationCode)}</strong> (zelfde
              koppeling, beide kanten).
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={busy}>
            {busy ? "Bezig…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
